// Routing — given a party's roster, who picks up the next step?
//
// Naive (human path): the originally-assigned primary person. Work waits
// for them no matter how long they're out. This is the dramatic reality
// the pitch points at: "Bob is on vacation, the work sits."
//
// Reroute (AI path): the person whose next-available-slot is soonest.
// Same function, but Planapus knows everyone in it and routes accordingly.

import type { Agent, World } from "./sim-world";

export const MODE_NAIVE = "naive";
export const MODE_REROUTE = "reroute";

export type SimMode = typeof MODE_NAIVE | typeof MODE_REROUTE;

export interface PickResult {
  agent: Agent | null;
  pickupHr: number;
}

/**
 * @returns the chosen agent and the earliest hour they can begin work
 *   (≥ arrivalHr).
 */
export function pickAssignee(
  world: World,
  partyId: string,
  arrivalHr: number,
  mode: SimMode,
): PickResult {
  const roster = world.agentsByParty.get(partyId);
  if (!roster || roster.length === 0) {
    return { agent: null, pickupHr: arrivalHr };
  }

  if (mode === MODE_REROUTE) {
    // Smallest next-available across the whole team. Ties go to the primary.
    let best: Agent | null = null;
    let bestT = Infinity;
    for (const a of roster) {
      const t = a.calendar.nextAvailable(arrivalHr);
      if (t < bestT - 1e-6) {
        bestT = t;
        best = a;
      } else if (Math.abs(t - bestT) < 1e-6 && a.isPrimary) {
        best = a;
      }
    }
    return { agent: best, pickupHr: bestT };
  }

  // Naive: the primary person waits no matter how long.
  const primary = roster.find(a => a.isPrimary) || roster[0];
  return { agent: primary, pickupHr: primary.calendar.nextAvailable(arrivalHr) };
}
